// src/store/userSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { ENDPOINTS } from "../../../js/api/_config";
import { getAndSetRefreshToken } from "../../../js/api/constants";
import { formatName, flattenPermissions } from "../../../components/common/constant/constant";


const token = sessionStorage.getItem('authToken') || localStorage.getItem('authToken');
const refreshToken = sessionStorage.getItem('refreshToken') || localStorage.getItem('refreshToken');
let role = 'admin'; // fallback

export const extractUserFromToken = (token) => {
  try {
    const decoded = jwtDecode(token);
    return {
      role: decoded.role,       // assuming JWT has 'role'
      name: decoded.name,       // optional
      email: decoded.email,     // optional
      sub: decoded.sub
    };
  } catch (error) {
    console.error('Invalid token:', error);
    return undefined;
  }
};

// Async thunk for fetching user profile
export const fetchUserProfile = createAsyncThunk(
  "user/fetchUserProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(ENDPOINTS.USER.PROFILE);
      return response.data.data;
    } catch (error) {
      console.error("Error fetching user profile:", error);
      return rejectWithValue(error.response?.data || "Failed to fetch user profile");
    }
  }
);

export const fetchUserPermission = createAsyncThunk(
  "user/fetchUserPermission",
  async (_, { rejectWithValue }) => {
    try {

      const userId = extractUserFromToken(token).sub;
      const response = await axios.get(ENDPOINTS.USER.PERMISSION, { params: { "UserId": userId } });

      const permission = response.data.data.data || [];
      let result = [];
      permission.forEach(item => {
        if (item.pageName && Array.isArray(item.children)) {

          const children = item.children.filter(x => x.isEnabled).map(child => child.childPermissionPageName);
          result.push(...children); // spread child items into array

          if (children.length > 0) {
            result.push(item.parentPageName);
          }
        }
        console.log("item", result);
      });

      return result;

    } catch (error) {
      console.error("Error fetching user profile:", error);
      return rejectWithValue(error.response?.data || "Failed to fetch user profile");
    }
  }
);


if (token) {
  role = extractUserFromToken(token)?.role;
}

const initialState = {
  token: token,
  refreshToken: refreshToken,
  isAuthenticated: !!token,
  userProfile: [],
  role: role,
  isLoading: true,
  error: null,
  permissions: null,
  isPermissionLoaded: false,
  sub: null
};

// Create the slice
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    login: (state, action) => {
      const { user, token, refreshToken, rememberMe } = action.payload;
      state.user = user;
      state.token = token;
      state.role = extractUserFromToken(token)?.role;
      state.sub = extractUserFromToken(token)?.sub;
      state.refreshToken = refreshToken;
      state.permissions = null;
      state.isPermissionLoaded = false;
      state.isAuthenticated = true;
      if (rememberMe) {
        sessionStorage.setItem('authToken', token);
        sessionStorage.setItem("refreshToken", refreshToken);
      } else {
        localStorage.setItem('authToken', token);
        localStorage.setItem("refreshToken", refreshToken);
      }
    },
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.refreshToken = null;
      state.isAuthenticated = false;
      state.permissions = null;
      state.isPermissionLoaded = false;
      localStorage.removeItem('authToken');
      localStorage.removeItem("refreshToken");
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem("refreshToken");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserProfile.pending, (state) => {
        return {
          ...state,
          isLoading: true,
          error: null
        };
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        return {
          ...state,
          isLoading: false,
          userProfile: action.payload
        };
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        return {
          ...state,
          isLoading: false,
          error: action.payload
        }
      }).addCase(fetchUserPermission.pending, (state) => {
        return {
          ...state,
          isLoading: true,
          error: null
        };
      })
      .addCase(fetchUserPermission.fulfilled, (state, action) => {
        return {
          ...state,
          isLoading: false,
          permissions: action.payload,
          isPermissionLoaded: true
        };
      })
      .addCase(fetchUserPermission.rejected, (state, action) => {
        return {
          ...state,
          isLoading: false,
          error: action.payload
        }
      });
  },
});

export const { login, logout } = userSlice.actions;
export default userSlice.reducer;
